import {
  StyleParamType,
  wixColorParam,
  wixFontParam,
  createStylesParams,
  createStylesParam,
  ISettingsColor,
} from '@wix/tpa-settings';

// WCAG AA for normal-size text.
const MIN_CONTRAST_RATIO = 4.5;

// Declared standalone so dropdownBackgroundColor can resolve against whatever
// the site owner actually set, not merely against these defaults.
const formBackgroundColor = createStylesParam('backgroundColor', {
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam('color-1'),
});

const formTextColor = createStylesParam('textColor', {
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam('color-5'),
});

// ISettingsColor.value is whatever color-string produced, e.g. 'rgb(17, 4, 4)'
// or 'rgba(17, 4, 4, 0)', so the first three numbers are always the channels.
const channelsOf = ({ value }: ISettingsColor): number[] => {
  const parsed = (value?.match(/[\d.]+/g) ?? []).slice(0, 3).map(Number);
  return parsed.length === 3 ? parsed : [0, 0, 0];
};

const relativeLuminance = (color: ISettingsColor): number => {
  const [r, g, b] = channelsOf(color).map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrastRatio = (a: ISettingsColor, b: ISettingsColor): number => {
  const [lighter, darker] = [relativeLuminance(a), relativeLuminance(b)].sort(
    (x, y) => y - x,
  );
  return (lighter + 0.05) / (darker + 0.05);
};

// Straight-line distance in RGB. Rough as a perceptual measure, but enough to
// answer "which palette colour looks most like this one".
const distanceBetween = (a: ISettingsColor, b: ISettingsColor): number => {
  const [ar, ag, ab] = channelsOf(a);
  const [br, bg, bb] = channelsOf(b);
  return Math.hypot(ar - br, ag - bg, ab - bb);
};

export type IStylesParams = {
  backgroundColor: StyleParamType.Color;
  titleFont: StyleParamType.Font;
  titleColor: StyleParamType.Color;
  paragraphFont: StyleParamType.Font;
  paragraphColor: StyleParamType.Color;
  textFont: StyleParamType.Font;
  textColor: StyleParamType.Color;
  buttonColor: StyleParamType.Color;
  buttonHoverColor: StyleParamType.Color;
  buttonBackgroundColorAndOpacity: StyleParamType.Color;
  buttonBorderWidth: StyleParamType.Number;
  buttonBorderRadius: StyleParamType.Number;
  selectedButtonColor: StyleParamType.Color;
  mainButtonBackgroundColorAndOpacity: StyleParamType.Color;
  mainButtonTextColor: StyleParamType.Color;
  mainButtonFont: StyleParamType.Font;
  mainButtonBorderColorAndOpacity: StyleParamType.Color;
  mainButtonBorderWidth: StyleParamType.Number;
  mainButtonBorderRadius: StyleParamType.Number;
  mainButtonHoverBackgroundColorAndOpacity: StyleParamType.Color;
  mainButtonHoverColorAndOpacity: StyleParamType.Color;
  mainButtonHoverTextColor: StyleParamType.Color;
  inputBackgroundColor: StyleParamType.Color;
  inputBackgroundColorHover: StyleParamType.Color;
  dropdownBackgroundColor: StyleParamType.Color;
  dropdownOptionTextColor: StyleParamType.Color;
  inputBorderColor: StyleParamType.Color;
  inputBorderColorHover: StyleParamType.Color;
  inputBorderWidth: StyleParamType.Number;
  inputBorderWidthHover: StyleParamType.Number;
  inputBorderRadius: StyleParamType.Number;
};

export default createStylesParams<IStylesParams>({
  backgroundColor: formBackgroundColor,
  titleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Heading-L', {
      size: 32,
    }),
  },
  titleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  paragraphFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-L', {
      size: 16,
    }),
  },
  paragraphColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  textFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-L', {
      size: 16,
    }),
  },
  textColor: formTextColor,
  buttonColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  buttonHoverColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-3'),
  },
  buttonBackgroundColorAndOpacity: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  buttonBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  buttonBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 4,
  },
  selectedButtonColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  mainButtonBackgroundColorAndOpacity: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'), // Primary action color
  },
  mainButtonTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'), // White/contrast text
  },
  mainButtonFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
  },
  mainButtonBorderColorAndOpacity: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  mainButtonBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  mainButtonBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 4,
  },
  mainButtonHoverBackgroundColorAndOpacity: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  mainButtonHoverColorAndOpacity: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  mainButtonHoverTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  // Input border defaults mirror the values wix-ui-tpa falls back to at rest,
  // and every hover default mirrors its non-hover counterpart, so hovering a
  // field is a no-op until a site owner changes it on purpose (SCHED-47905).
  //
  // The background is fully transparent rather than color-1 so a field sits on
  // the section background, exactly as its label already does. Pinning it to
  // color-1 meant a site whose Color 1 is dark painted a dark field behind text
  // that follows the site's text color, which is chosen for the (light) section
  // behind it - black on near-black, a 1.04 contrast ratio (SCHED-48268).
  inputBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 0),
  },
  inputBackgroundColorHover: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 0),
  },
  // form-viewer resolves the dropdown popover as
  //   var(--dropdownBackgroundColor, var(--inputBackgroundColor))
  // so leaving this undefined made the floating menu borrow the input surface.
  // It reads the RGB triple but not the opacity, so a transparent input still
  // painted an opaque menu in color-1. A popover floats over page content, so
  // it cannot follow a transparent field; it tracks the form background
  // instead, which is the surface the rest of the form is styled against
  // (SCHED-48268).
  // form-viewer resolves the popover's two surfaces as
  //   var(--dropdownBackgroundColor,  var(--inputBackgroundColor))
  //   var(--dropdownOptionTextColor,  var(--inputValueColor))
  // Leaving them undefined made the menu borrow the input surface, taking its
  // RGB triple but not its opacity, so a transparent field still painted an
  // opaque menu in color-1 (SCHED-48268).
  //
  // The menu always takes the form background verbatim, so it reads as part of
  // the form. When that surface cannot carry the form's text colour, the TEXT
  // moves rather than the surface: recolouring the panel makes the menu jump
  // away from the form, while recolouring the text leaves the shape intact.
  dropdownBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: (params) => params.getStyleParamValue(formBackgroundColor),
  },
  dropdownOptionTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: (params) => {
      const surface = params.getStyleParamValue(formBackgroundColor);
      const optionText = params.getStyleParamValue(formTextColor);

      if (contrastRatio(optionText, surface) >= MIN_CONTRAST_RATIO) {
        return optionText;
      }

      // Stay on the site's palette rather than inventing a colour, and take
      // the readable slot closest to the form's own text colour so the shift
      // is as small as it can be.
      const palette = (params.colors ?? [])
        .map(({ reference }: { reference?: string }) => reference)
        .filter((reference): reference is string => Boolean(reference))
        .map((reference) => wixColorParam(reference)(params));

      const readable = palette.filter(
        (color) => contrastRatio(color, surface) >= MIN_CONTRAST_RATIO,
      );

      if (readable.length) {
        return readable.sort(
          (a, b) =>
            distanceBetween(a, optionText) - distanceBetween(b, optionText),
        )[0];
      }

      // Nothing in the palette reads on this surface. Take the best available.
      return (
        palette.sort(
          (a, b) => contrastRatio(b, surface) - contrastRatio(a, surface),
        )[0] ?? optionText
      );
    },
  },
  inputBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  inputBorderColorHover: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  inputBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  inputBorderWidthHover: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  inputBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
});
